import styled from 'styled-components';
import Pica from 'pica';
import { useRef, useState, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Modal, ModalTitle, ModalBody, ModalFooter, ModalFooterAlignment } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { DrawInputField, DrawingInputFieldRef } from 'design-system/src/components/DrawInputField/DrawInputField';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { client } from '../../../api';

const SpacedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export type SignFormModalProps = {
  onClose: () => void;
  signatureWidth?: number;
  signatureHeight?: number;
};

/**
 * Modal with a drawing input field for getting a signature from the user.
 */
// 200 x 25 is a carefully chosen aspect ratio that allows for a signature to be inserted into many different
// pandadoc signature fields - do not change unless you have a super good reason!
export const SignFormModal = ({ onClose, signatureWidth = 200, signatureHeight = 25 }: SignFormModalProps) => {
  const [step, setStep] = useState<'sign' | 'success'>('sign');
  const modalSignContentRef = useRef<HTMLDivElement>(null);
  const drawingInputFieldRef = useRef<DrawingInputFieldRef>(null);
  const [hasDrawn, setHasDrawn] = useState<boolean>(false);
  const [drawFieldHasError, setDrawFieldHasError] = useState<boolean>(false);
  const navigate = useNavigate();
  const signatureErrorId = useId();
  const { enrollmentId } = useParams();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submitPatientSignatureMutation = useMutation({
    mutationFn: async (resizedSigFile: File) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking patient signature submission, no real request sent.');

        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      if (!resizedSigFile) {
        throw new Error('No signature file available');
      }

      const formData = new FormData();
      formData.append('files[]', resizedSigFile);

      await client.post(`enrollments/${enrollmentId}/submit-patient-signature`, formData);
    },
    onSuccess: () => {
      setStep('success');
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const resizeFile = async (file: SVGElement) => {
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = signatureWidth;
    resizedCanvas.height = signatureHeight;

    // Clone the SVG element to avoid modifying the original
    const svgClone = file.cloneNode(true) as SVGElement;
    // Ensure the SVG has a transparent background
    svgClone.setAttribute('style', 'background-color: transparent;');

    // Get the original dimensions of the SVG
    const originalWidth = file.getBoundingClientRect().width;
    const originalHeight = file.getBoundingClientRect().height;

    // Calculate the scale factor to fit within target dimensions while maintaining aspect ratio
    const scaleX = signatureWidth / originalWidth;
    const scaleY = signatureHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY);

    // Set the viewBox to the original dimensions
    svgClone.setAttribute('viewBox', `0 0 ${originalWidth} ${originalHeight}`);
    // Set the width and height to the scaled dimensions
    svgClone.setAttribute('width', (originalWidth * scale).toString());
    svgClone.setAttribute('height', (originalHeight * scale).toString());

    const svgString = new XMLSerializer().serializeToString(svgClone);

    return new Promise<File>((resolve) => {
      let img = new Image();
      img.onload = function () {
        resizedCanvas.width = signatureWidth;
        resizedCanvas.height = signatureHeight;

        const pica = Pica();
        pica
          // the empty object can be used to pass in quality and sharpening options
          // https://github.com/nodeca/pica?tab=readme-ov-file#resizebufferoptions---promise
          .resize(img, resizedCanvas, {
            unsharpAmount: 160,
            unsharpRadius: 0.6,
            unsharpThreshold: 1,
          })
          .then((result) => pica.toBlob(result, 'image/png'))
          .then((resizedBlob) => {
            const file = new File([resizedBlob], `patient-signature-${enrollmentId}-${Date.now()}-original-size.png`, {
              type: 'image/png',
              lastModified: Date.now(),
            });
            resolve(file);
          })
          .catch((error) => {
            console.error('Error resizing file:', error);
            Sentry.captureException(error, {
              extra: {
                enrollmentId,
              },
            });
            toast.custom(() => <StandardErrorToast />);
          });
      };

      img.onerror = function (error) {
        console.error('Error loading image:', error);
        Sentry.captureException(error, {
          extra: {
            enrollmentId,
          },
        });
      };

      // set the img src to the svg string and trigger the resize logic above
      img.src = 'data:image/svg+xml;base64,' + btoa(svgString);
    });
  };

  const handleSubmit = async () => {
    if (!hasDrawn) {
      setDrawFieldHasError(true);
      return;
    }

    const svgElement = drawingInputFieldRef.current?.svg;
    if (!svgElement) return;

    try {
      const resizedFile = await resizeFile(svgElement);
      submitPatientSignatureMutation.mutate(resizedFile);
    } catch (error) {
      console.error('Error resizing file:', error);
      Sentry.captureException(error, {
        extra: {
          enrollmentId,
        },
      });
      toast.custom(() => <StandardErrorToast />);
    }
  };

  const signStep = (
    <>
      <ModalTitle>Add patient signature</ModalTitle>

      <ModalBody ref={modalSignContentRef}>
        <Typography styledAs="bodyLargeDMSans">After signing, this form will be locked to ensure accuracy.</Typography>
        <div>
          <DrawInputField
            label="Signature"
            hideLabel
            drawingInputFieldRef={drawingInputFieldRef}
            onDrawingChange={(data) => {
              if (data.length > 0) {
                setHasDrawn(true);
                setDrawFieldHasError(false);
              }
            }}
            onClearDrawing={() => setHasDrawn(false)}
            hasError={drawFieldHasError}
            aria-describedby={signatureErrorId}
            aspectRatio={`${signatureWidth} / ${signatureHeight}`}
          />
          <ErrorMessage id={signatureErrorId} visible={drawFieldHasError} textStyledAs="bodyExtraSmallSpaceGrotesk">
            Signature required
          </ErrorMessage>
        </div>

        <Typography styledAs="bodyExtraSmallDMSans" color="var(--dark-gray)">
          By electronically signing this document, I agree that my signature and initials are the equivalent of my
          handwritten signature and are considered originals on all documents, including legally binding contracts.
        </Typography>
      </ModalBody>

      <ModalFooter align={ModalFooterAlignment.SpaceBetween}>
        <Button variant="secondary" onClick={onClose} disabled={submitPatientSignatureMutation.isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} isLoading={submitPatientSignatureMutation.isPending}>
          Submit patient signature
        </Button>
      </ModalFooter>
    </>
  );

  const successStep = (
    <>
      <ModalTitle>Thanks for signing!</ModalTitle>
      <ModalBody>
        <SpacedContent>
          <Typography styledAs="bodyLargeDMSans">
            This form is now pending. We'll complete the remaining sections using the information from the prescription
            and notify you when the form is ready to be reviewed for provider signature.
          </Typography>
          <Typography styledAs="bodyLargeDMSans">
            You can review this and other pending forms at the top of the Prescriptions page.
          </Typography>
        </SpacedContent>
      </ModalBody>
      <ModalFooter>
        <Button to="/prescriptions">Return to Prescriptions</Button>
        <Button variant="secondary" onClick={() => location.reload()}>
          View pending form
        </Button>
      </ModalFooter>
    </>
  );

  return (
    <Modal onClose={step === 'sign' ? onClose : () => navigate('/prescriptions')} maxWidth="800px">
      {step === 'sign' ? signStep : successStep}
    </Modal>
  );
};

export default SignFormModal;
